import React, { useEffect, useMemo, useRef, useState } from 'react';
import { fill, initials, matchesQuery } from './labels';

// The "Lihat daftar" target. There is no participants index route in admin scoped to a
// day, so the list is shown here rather than linking away (plan gap G12).
//
// The rows are fetched when this opens instead of riding on the 60s /daily poll: the
// full list can run to hundreds of rows that nobody looks at most of the time.
export default function PendingListModal({ date, hospitalId, count, onClose, onRemind, labels }) {
  var [query, setQuery] = useState('');
  var [rows, setRows] = useState(null);
  var [error, setError] = useState(false);
  var dialogRef = useRef(null);

  useEffect(function () {
    function onKeyDown(event) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    if (dialogRef.current) dialogRef.current.focus();
    return function () { document.removeEventListener('keydown', onKeyDown); };
  }, [onClose]);

  useEffect(function () {
    var cancelled = false;
    var params = new URLSearchParams({ date: date });
    if (hospitalId) params.set('hospital_id', hospitalId);

    fetch('/api/ehc/monitoring/pending?' + params.toString(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).
      then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      }).
      then(function (json) {
        if (!cancelled) setRows(json.participants || []);
      }).
      catch(function () {
        if (!cancelled) setError(true);
      });

    return function () { cancelled = true; };
  }, [date, hospitalId]);

  var filtered = useMemo(function () {
    if (!rows) return [];
    return rows.filter(function (participant) { return matchesQuery(participant, query); });
  }, [rows, query]);

  return (
    <div className="ehc-monitoring__modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="ehc-monitoring__modal ehc-monitoring__modal--wide"
        role="dialog"
        aria-modal="true"
        aria-label={labels.title}
        tabIndex={-1}
        ref={dialogRef}
        onClick={function (event) { event.stopPropagation(); }}
      >
        <div className="ehc-monitoring__modal-header">
          <h5>{labels.title}</h5>
          <p className="ehc-monitoring__sub">{fill(labels.sub, { count: count })}</p>
        </div>

        <div className="ehc-monitoring__modal-body">
          <div className="form-group mb-0 ehc-monitoring__search">
            <input
              type="search"
              className="form-control"
              placeholder={labels.search_placeholder}
              aria-label={labels.search_placeholder}
              value={query}
              onChange={function (event) { setQuery(event.target.value); }}
              disabled={!rows}
            />
            <i className="form-group__bar" />
          </div>

          {error && <p className="text-danger mb-0 mt-3">{labels.error}</p>}

          {!error && rows === null && (
            <p className="text-muted mb-0 mt-3" aria-busy="true">{labels.loading}</p>
          )}

          {!error && rows !== null && filtered.length === 0 && (
            <p className="text-muted mb-0 mt-3">{labels.empty}</p>
          )}

          {!error && filtered.length > 0 && (
            <div className="ehc-monitoring__pending-list">
              <table className="table ehc-monitoring__table">
                <thead>
                  <tr>
                    <th>{labels.table.participant}</th>
                    <th>{labels.table.company}</th>
                    <th>{labels.table.employee_id}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(function (participant) {
                    return (
                      <tr key={participant.id}>
                        <td>
                          <span className="ehc-monitoring__avatar">{initials(participant.name)}</span>
                          {participant.name}
                        </td>
                        <td>{participant.company}</td>
                        <td>{participant.employee_id || labels.table.employee_id_missing}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="ehc-monitoring__modal-footer">
          <button type="button" className="btn btn-link" onClick={onClose}>
            {labels.close}
          </button>
          <button type="button" className="btn btn-primary" onClick={onRemind}>
            {labels.remind}
          </button>
        </div>
      </div>
    </div>
  );
}
